/**
 * SnelleResetSession — 3-minute guided reset session.
 * Cycles through: Stop het verhaal → Ademhaling → Grond jezelf → Sluit af
 */
import React, { useState, useEffect, useRef } from 'react';
import { C, _T } from '../../../shared/theme';
import { s_GB } from '../../../shared/styles';

const COLOR   = '#7B6FD0';
const TOTAL   = 180; // seconds

// Phase boundaries (seconds elapsed)
const PHASES = [
  { id: 0, start: 0,   end: 40  },  // Stop het verhaal
  { id: 1, start: 40,  end: 110 },  // Ademhaling
  { id: 2, start: 110, end: 160 },  // Grond jezelf
  { id: 3, start: 160, end: 180 },  // Sluit af
];

// ── Breathing circle ─────────────────────────────────────────────────────────
function BreathCircle({ active }) {
  const [bs, setBs] = useState({ phase: 'in', progress: 0, secs: 4 });
  const ref = useRef(null);

  useEffect(() => {
    if (!active) { clearInterval(ref.current); return; }
    const DUR = { in: 4000, out: 6000 };
    let phase = 'in';
    let phaseStart = Date.now();

    ref.current = setInterval(() => {
      const now     = Date.now();
      const elapsed = now - phaseStart;
      if (elapsed >= DUR[phase]) {
        phase      = phase === 'in' ? 'out' : 'in';
        phaseStart = now;
      }
      const progress = Math.min(1, (now - phaseStart) / DUR[phase]);
      const secsLeft = Math.ceil((DUR[phase] - (now - phaseStart)) / 1000);
      setBs({ phase, progress, secs: Math.max(0, secsLeft) });
    }, 100);

    return () => clearInterval(ref.current);
  }, [active]);

  const scale = bs.phase === 'in' ? 0.45 + 0.55 * bs.progress : 1.0 - 0.55 * bs.progress;
  const BASE  = 80;
  const r     = BASE * scale;
  const glow  = Math.round(scale * 40);
  const label = bs.phase === 'in' ? 'Adem in — 4 sec' : 'Adem uit — 6 sec';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '16px 0 8px' }}>
      <div style={{ position: 'relative', width: BASE * 2 + 60, height: BASE * 2 + 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* ambient rings */}
        <div style={{ position: 'absolute', width: BASE * 2 + 60, height: BASE * 2 + 60, borderRadius: '50%', border: `1px solid ${COLOR}18` }} />
        <div style={{ position: 'absolute', width: BASE * 2 + 30, height: BASE * 2 + 30, borderRadius: '50%', border: `1px solid ${COLOR}10` }} />
        {/* main circle */}
        <div style={{
          width: r * 2, height: r * 2, borderRadius: '50%',
          background: `radial-gradient(circle, ${COLOR}30 0%, ${COLOR}08 70%)`,
          border: `2px solid ${COLOR}${Math.round(scale * 99).toString(16).padStart(2, '0')}`,
          boxShadow: `0 0 ${glow}px ${COLOR}40, 0 0 ${glow * 2}px ${COLOR}15`,
          transition: 'width .15s ease-out, height .15s ease-out, box-shadow .15s ease',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ color: COLOR, fontSize: Math.max(10, Math.round(scale * 13)), fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', padding: '0 6px', lineHeight: 1.3 }}>
            {label}
          </div>
          {bs.secs > 0 && (
            <div style={{ color: '#fff', fontSize: Math.max(16, Math.round(scale * 26)), fontWeight: 700, fontFamily: 'Inter,sans-serif', marginTop: 4 }}>
              {bs.secs}
            </div>
          )}
        </div>
      </div>
      <div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>6–8 rondes · ontspan je schouders</div>
    </div>
  );
}

// ── Phase 0: cycling mantra phrases ──────────────────────────────────────────
const MANTRA_NL = ['Stop.', 'Dit is een verhaal.', 'Niet mijn waarheid.'];
const MANTRA_EN = ['Stop.', 'This is a story.', 'Not my truth.'];
// How long each phrase stays visible (ms) before fading out
const PHRASE_SHOW = 2600;
const PHRASE_OUT  = 700;

function StopVerhaalPhase({ secsLeft, phaseEnd, nl, running }) {
  const phrases  = nl ? MANTRA_NL : MANTRA_EN;
  const [pIdx,   setPIdx]   = useState(0);
  const [phase,  setPhase]  = useState('in'); // 'in' | 'out'
  const phaseTotal = phaseEnd; // seconds total for this phase
  const elapsed    = phaseTotal - secsLeft;
  // countdown within this phase (0 at start, phaseEnd at end)
  const phaseCountdown = Math.max(0, secsLeft); // overall timer

  useEffect(() => {
    if (!running) return;
    if (phase === 'in') {
      const t = setTimeout(() => setPhase('out'), PHRASE_SHOW);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setPIdx(i => (i + 1) % phrases.length);
        setPhase('in');
      }, PHRASE_OUT);
      return () => clearTimeout(t);
    }
  }, [phase, running]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '28px 20px 22px' }}>
      <style>{`
        @keyframes sr-win  { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes sr-wout { from { opacity:1; filter:blur(0) } to { opacity:0; filter:blur(8px) } }
      `}</style>
      <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 20 }}>
        STAP 1 — {nl ? 'STOP HET VERHAAL' : 'STOP THE STORY'}
      </div>

      {/* Animated phrase */}
      <div style={{ minHeight: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
        <div
          key={`${pIdx}-${phase}`}
          style={{
            fontFamily: 'Georgia,serif',
            fontSize: pIdx === 0 ? 48 : 22,
            fontWeight: pIdx === 0 ? 800 : 600,
            color: pIdx === 0 ? '#fff' : `${COLOR}ee`,
            textAlign: 'center',
            lineHeight: 1.3,
            fontStyle: pIdx === 0 ? 'normal' : 'italic',
            animation: phase === 'in'
              ? 'sr-win 0.65s cubic-bezier(.22,1,.36,1) both'
              : `sr-wout ${PHRASE_OUT}ms ease both`,
          }}
        >
          {phrases[pIdx]}
        </div>
      </div>

      {/* Instruction */}
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, textAlign: 'center', marginBottom: 14, lineHeight: 1.6 }}>
        {nl ? 'Zeg dit in je hoofd · herhaal zo vaak als nodig' : 'Say this in your head · repeat as often as needed'}
      </div>

      {/* Context tags */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 14 }}>
        {(nl
          ? ['druk op je borst', 'gedachten draaien', 'dit klopt niet']
          : ['chest pressure', 'spinning thoughts', 'something feels off']
        ).map(t => (
          <span key={t} style={{ background: `${COLOR}15`, border: `1px solid ${COLOR}30`, borderRadius: 20, color: COLOR, fontSize: 10, padding: '3px 10px' }}>{t}</span>
        ))}
      </div>

      {/* Non-prominent countdown — shows seconds remaining in this phase */}
      <div style={{ textAlign: 'right', color: 'rgba(255,255,255,0.18)', fontSize: 10, fontFamily: 'monospace' }}>
        {phaseCountdown}s
      </div>
    </div>
  );
}

// ── Grounding inputs ──────────────────────────────────────────────────────────
function GroundingInputs({ sees, setSees, feels, setFeels, hears, setHears, nl }) {
  const inputStyle = {
    background: '#0f172a', border: `1px solid ${COLOR}44`, borderRadius: 8,
    color: '#fff', fontSize: 12, padding: '7px 10px', fontFamily: 'inherit',
    outline: 'none', width: '100%', marginBottom: 4,
  };
  const groupLabel = (emoji, text) => (
    <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4, marginTop: 8 }}>
      {emoji} {text}
    </div>
  );

  return (
    <div style={{ marginTop: 8 }}>
      {groupLabel('👁', nl ? '5 dingen die je ZIET' : '5 things you SEE')}
      {sees.map((v, i) => (
        <input key={i} style={inputStyle} value={v} placeholder={`${i + 1}.`}
          onChange={e => { const a = [...sees]; a[i] = e.target.value; setSees(a); }} />
      ))}
      {groupLabel('🤚', nl ? '4 dingen die je VOELT' : '4 things you FEEL')}
      {feels.map((v, i) => (
        <input key={i} style={inputStyle} value={v} placeholder={`${i + 1}.`}
          onChange={e => { const a = [...feels]; a[i] = e.target.value; setFeels(a); }} />
      ))}
      {groupLabel('👂', nl ? '3 dingen die je HOORT' : '3 things you HEAR')}
      {hears.map((v, i) => (
        <input key={i} style={inputStyle} value={v} placeholder={`${i + 1}.`}
          onChange={e => { const a = [...hears]; a[i] = e.target.value; setHears(a); }} />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SnelleResetSession({ lang, onFinish }) {
  const nl = lang === 'nl';
  const [secsLeft, setSecsLeft]   = useState(TOTAL);
  const [running, setRunning]     = useState(true);
  const [done, setDone]           = useState(false);
  const [sees,  setSees]          = useState(['', '', '', '', '']);
  const [feels, setFeels]         = useState(['', '', '', '']);
  const [hears, setHears]         = useState(['', '', '']);
  const timerRef = useRef(null);

  const elapsed = TOTAL - secsLeft;
  const phase   = PHASES.findIndex(p => elapsed >= p.start && elapsed < p.end);
  const curPhase = phase === -1 ? 3 : phase;

  // Countdown timer
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setSecsLeft(s => {
        if (s <= 1) { clearInterval(timerRef.current); setDone(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const saveAndFinish = () => {
    const entry = {
      id:      Date.now(),
      type:    'snelle_reset',
      label:   nl ? 'Snelle Reset' : 'Quick Reset',
      icon:    '🔄',
      color:   COLOR,
      date:    new Date().toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      grounding: {
        sees:  sees.filter(Boolean),
        feels: feels.filter(Boolean),
        hears: hears.filter(Boolean),
      },
    };
    try {
      const prev = JSON.parse(localStorage.getItem('sl_saved_sessions') || '[]');
      prev.unshift(entry);
      if (prev.length > 50) prev.length = 50;
      localStorage.setItem('sl_saved_sessions', JSON.stringify(prev));
    } catch {}
    onFinish({ feelings: ['Rustiger', 'Meer afstand'], aiComment: null });
  };

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  const pct  = ((TOTAL - secsLeft) / TOTAL) * 100;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (done || secsLeft === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✨</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: '#fff', marginBottom: 8 }}>
          {nl ? 'Sessie voltooid' : 'Session complete'}
        </div>
        <div style={{ color: COLOR, fontSize: 15, fontStyle: 'italic', marginBottom: 24, padding: '0 20px' }}>
          "Ik hoef dit nu niet op te lossen."
        </div>
        <button onClick={saveAndFinish} style={s_GB({ fontSize: 14, padding: '12px 28px' })}>
          {nl ? 'Opslaan & afsluiten' : 'Save & finish'}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Timer + progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,.08)', borderRadius: 4 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${COLOR},#9b5cf6)`, borderRadius: 4, transition: 'width 1s linear' }} />
        </div>
        <div style={{ color: C.silver, fontSize: 12, fontFamily: 'monospace', minWidth: 36 }}>
          {mins}:{String(secs).padStart(2, '0')}
        </div>
        <button onClick={() => setRunning(r => !r)} style={{ background: 'none', border: `1px solid rgba(255,255,255,.12)`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
          {running ? '⏸' : '▶'}
        </button>
      </div>

      {/* Phase 0 — Stop het verhaal (cycling mantra phrases) */}
      {curPhase === 0 && <StopVerhaalPhase secsLeft={secsLeft} phaseEnd={PHASES[0].end} nl={nl} running={running} />}

      {/* Phase 1 — Ademhaling */}
      {curPhase === 1 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 2 — TERUG IN JE LICHAAM
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#fff', marginBottom: 4 }}>
            Leg je hand op je borst
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 4 }}>
            Kalm je zenuwstelsel direct — adem met de cirkel mee.
          </div>
          <BreathCircle active={running && curPhase === 1} />
        </div>
      )}

      {/* Phase 2 — Grond jezelf */}
      {curPhase === 2 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 3 — GROND JEZELF
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#fff', marginBottom: 4 }}>
            Haal je uit je hoofd → terug in het moment
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 8 }}>
            Kijk om je heen en benoem wat je waarneemt.
          </div>
          <GroundingInputs
            sees={sees}  setSees={setSees}
            feels={feels} setFeels={setFeels}
            hears={hears} setHears={setHears}
            nl={nl}
          />
        </div>
      )}

      {/* Phase 3 — Sluit af */}
      {curPhase === 3 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 16 }}>
            AFSLUITING
          </div>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🌿</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: '#fff', lineHeight: 1.6, fontStyle: 'italic', padding: '0 16px' }}>
            "Ik hoef dit nu<br />niet op te lossen."
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 16 }}>
            Adem nog één keer diep in en uit. Dan ben je klaar.
          </div>
        </div>
      )}

      {/* Skip to finish early */}
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <button onClick={() => { clearInterval(timerRef.current); setDone(true); }}
          style={{ background: 'none', border: 'none', color: C.silver, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
          {nl ? 'Sessie afsluiten' : 'End session'}
        </button>
      </div>
    </div>
  );
}
